import { mui } from "@/mui";
import {
  PhoneIcon,
  PhoneOff,
  PhoneOffIcon,
  CrownIcon,
  MicIcon,
  MicOffIcon,
  VideoIcon,
  VideoOffIcon,
  MonitorUp,
  Volume2Icon,
  CircleDot,
  Settings,
} from "lucide-react";
import type { MediaSession } from "@/lib/av-stream-types";
import { useMediaSessions } from "@/hooks/useMediaSessions";
import { AVSettingsDialog } from "./av-settings-dialog";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useMobyNodeClient, useSession } from "../provider";
import { useEffect, useRef, useState, useCallback } from "react";
import { getPeerAvatarColor, getPeerBannerColor, renderPeerAvatarEmoji, type RemotePeerProfile } from "@/lib/peer-profile-utils";

// ─── Filmstrip thumbnail for a single participant ────────────────────────────
interface FilmstripTileProps {
  session: MediaSession;
  peerAddress: string;
  contactName: string;
  contactInitials: string;
  contactProfile?: RemotePeerProfile;
  isLocal: boolean;
  isSelected: boolean;
  isMuted: boolean;
  onClick: () => void;
}

function FilmstripTile({
  session,
  peerAddress,
  contactName,
  contactInitials,
  contactProfile,
  isLocal,
  isSelected,
  isMuted,
  onClick,
}: FilmstripTileProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const userPreferences = useUserPreferences();

  // Determine if this participant has a visual stream (video OR screen share)
  const hasVideo = isLocal
    ? (session.videoEnabled || session.screenShareEnabled === true)
    : (session.remoteVideoEnabled?.get(peerAddress) ?? false) ||
      (session.remoteStreams.get(peerAddress)?.getVideoTracks().some(t => t.readyState === 'live') ?? false);

  // Attach video stream to thumbnail
  useEffect(() => {
    if (!videoRef.current) return;
    if (isLocal && session.localStream) {
      videoRef.current.srcObject = session.localStream;
      videoRef.current.play().catch(() => {});
    } else if (!isLocal) {
      const remote = session.remoteStreams.get(peerAddress);
      if (remote) {
        videoRef.current.srcObject = remote;
        videoRef.current.play().catch(() => {});
      }
    }
  }, [session, peerAddress, isLocal, hasVideo, session.screenShareEnabled]);

  return (
    <button
      onClick={onClick}
      className={`relative shrink-0 w-20 h-14 rounded-lg overflow-hidden transition-all ${
        isSelected
          ? "ring-2 ring-primary ring-offset-1 ring-offset-black/50"
          : "ring-1 ring-white/20 hover:ring-white/40"
      }`}
      title={contactName}
    >
      {/* Video or avatar fallback */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`w-full h-full object-cover ${hasVideo ? "" : "hidden"}`}
      />
      {!hasVideo && (
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            backgroundColor: isLocal
              ? userPreferences.bannerColor
              : getPeerBannerColor(contactProfile),
          }}
        >
          <div
            className="rounded-full w-8 h-8 flex items-center justify-center text-white text-xs font-medium"
            style={{
              backgroundColor: isLocal
                ? userPreferences.avatarColor
                : getPeerAvatarColor(contactProfile),
            }}
          >
            {contactInitials}
            {renderPeerAvatarEmoji(
              isLocal ? ({ avatarEmoji: userPreferences.avatarEmoji } as any) : contactProfile,
              "sm",
            )}
          </div>
        </div>
      )}

      {/* Name label */}
      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/60 to-transparent px-1 pb-0.5 pt-2">
        <span className="text-white text-[10px] leading-tight truncate block">
          {isLocal ? "You" : contactName.split(" ")[0]}
        </span>
      </div>

      {/* Muted indicator */}
      {isMuted && (
        <div className="absolute top-0.5 right-0.5 bg-error/80 rounded-full p-0.5">
          <MicOffIcon size={8} className="text-white" />
        </div>
      )}
    </button>
  );
}

// ─── Main enlarged stream view ───────────────────────────────────────────────
interface MainStreamViewProps {
  session: MediaSession;
  peerAddress: string;
  contactName: string;
  contactInitials: string;
  contactProfile?: RemotePeerProfile;
  isLocal: boolean;
}

function MainStreamView({
  session,
  peerAddress,
  contactName,
  contactInitials,
  contactProfile,
  isLocal,
}: MainStreamViewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [remoteStreamUpdate, setRemoteStreamUpdate] = useState(0);
  const [frequencyData, setFrequencyData] = useState<number[]>(new Array(16).fill(0));
  const userPreferences = useUserPreferences();

  // Show video element when video OR screen share is active
  const hasVideo = isLocal
    ? (session.videoEnabled || session.screenShareEnabled === true)
    : (session.remoteVideoEnabled?.get(peerAddress) ?? false) ||
      (session.remoteStreams.get(peerAddress)?.getVideoTracks().some(t => t.readyState === 'live') ?? false);

  // Listen for remote stream additions
  useEffect(() => {
    const handleRemoteStreamAdded = (event: CustomEvent) => {
      if (event.detail.peerAddress === peerAddress && !isLocal) {
        setRemoteStreamUpdate((prev) => prev + 1);
      }
    };
    window.addEventListener("av-remote-stream-added", handleRemoteStreamAdded as EventListener);
    return () => {
      window.removeEventListener("av-remote-stream-added", handleRemoteStreamAdded as EventListener);
    };
  }, [peerAddress, isLocal]);

  // Attach video stream
  useEffect(() => {
    if (!videoRef.current) return;
    const el = videoRef.current;

    if (isLocal && session.localStream) {
      el.srcObject = session.localStream;
      el.play().catch(() => {});
    } else if (!isLocal) {
      const remote = session.remoteStreams.get(peerAddress);
      if (remote) {
        el.srcObject = remote;
        el.play().catch((err) => {
          if (err.name !== "AbortError") {
            console.error("[AV_PANEL] Main stream play failed", err);
          }
        });
      }
    }
  }, [session, peerAddress, isLocal, remoteStreamUpdate, hasVideo, session.screenShareEnabled, session.localStream]);

  // Audio visualization (16 bars for enlarged view)
  useEffect(() => {
    const stream = isLocal ? session.localStream : session.remoteStreams.get(peerAddress);
    if (!stream) return;

    try {
      const ctx = new AudioContext();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.7;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      audioContextRef.current = ctx;
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const update = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);

        const bars: number[] = [];
        const bins = dataArray.length;
        const barsCount = 16;
        const binSize = Math.floor(bins / barsCount);

        for (let i = 0; i < barsCount; i++) {
          const slice = dataArray.slice(i * binSize, (i + 1) * binSize);
          const avg = slice.reduce((s, v) => s + v, 0) / slice.length;
          const boost = i >= 3 && i <= 10 ? 1.8 : 1.4;
          bars.push(Math.min(100, (avg / 255) * 100 * boost));
        }

        setFrequencyData(bars);
        animationFrameRef.current = requestAnimationFrame(update);
      };
      update();
    } catch {
      /* audio context not available */
    }

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, [session.localStream, session.remoteStreams, peerAddress, isLocal]);

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden bg-black">
      {/* Video */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className={`w-full h-full object-cover ${hasVideo ? "" : "hidden"}`}
      />

      {/* Audio-only avatar fallback */}
      {!hasVideo && (
        <div
          className="w-full h-full flex flex-col items-center justify-center relative"
          style={{
            backgroundColor: isLocal
              ? userPreferences.bannerColor
              : getPeerBannerColor(contactProfile),
          }}
        >
          <div
            className="relative rounded-full w-32 h-32 flex items-center justify-center text-white z-10"
            style={{
              backgroundColor: isLocal
                ? userPreferences.avatarColor
                : getPeerAvatarColor(contactProfile),
            }}
          >
            <span className="text-5xl font-medium">{contactInitials}</span>
            {renderPeerAvatarEmoji(
              isLocal ? ({ avatarEmoji: userPreferences.avatarEmoji } as any) : contactProfile,
              "lg",
            )}
          </div>

          {/* 16-bar frequency visualization */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-end gap-[3px] h-16">
            {frequencyData.map((value, i) => {
              const bannerColor = (isLocal ? userPreferences.bannerColor : getPeerBannerColor(contactProfile)) || "#6200EE";
              const avatarColor = (isLocal ? userPreferences.avatarColor : getPeerAvatarColor(contactProfile)) || "#03DAC6";
              return (
                <div
                  key={i}
                  className="w-2 rounded-t-sm transition-all duration-75"
                  style={{
                    height: `${Math.max(4, (value / 100) * 64)}px`,
                    background: `linear-gradient(to top, ${bannerColor}, ${avatarColor}cc)`,
                    filter: "blur(0.5px)",
                  }}
                />
              );
            })}
          </div>

          {/* Name overlay */}
          <div className="absolute top-4 left-4">
            <div className="bg-black/40 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5">
              {session.isGroupCall && session.initiatorAddress === peerAddress && (
                <CrownIcon size={14} className="text-tertiary" />
              )}
              {isLocal ? "You" : contactName}
            </div>
          </div>

          {/* Quality badge */}
          <div className="absolute top-4 right-4">
            <div className="bg-black/40 backdrop-blur-sm text-white/70 px-2 py-1 rounded-full text-xs flex items-center gap-1">
              <Volume2Icon size={12} />
              {session.quality === 0 ? "Low" : session.quality === 1 ? "Med" : session.quality === 2 ? "High" : "Auto"}
            </div>
          </div>
        </div>
      )}

      {/* Reconnecting overlay */}
      {session.state === "connecting" && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <div className="text-white text-lg font-medium animate-pulse">Reconnecting...</div>
        </div>
      )}

      {/* Hidden audio for local visualization */}
      {isLocal && <audio ref={audioRef} muted autoPlay playsInline />}
    </div>
  );
}

// ─── AVStreamPanel — main export ─────────────────────────────────────────────

/**
 * AV Stream Panel — right-side panel matching ChatPanel layout
 *
 * Design:
 *  - Single enlarged main stream (selected participant)
 *  - Floating filmstrip overlay with all participants (including self)
 *  - Bottom button bar: Mute, Video, Screen Share, End Call, Record (placeholder), Settings (placeholder)
 *  - Self hidden from filmstrip when muted AND enough participants
 */
export function AVStreamPanel() {
  const node = useMobyNodeClient();
  const session = useSession();
  const mediaSessions = useMediaSessions();
  const userPreferences = useUserPreferences();

  // Local control state
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isTogglingVideo, setIsTogglingVideo] = useState(false);
  const [isTogglingScreenShare, setIsTogglingScreenShare] = useState(false);
  const [selectedPeerAddress, setSelectedPeerAddress] = useState<string | null>(null);
  const [showFilmstrip, setShowFilmstrip] = useState(true);
  const [callDuration, setCallDuration] = useState("0:00");

  // Only show ACTIVE streams
  const activeSessions = mediaSessions.filter((s) => s.session.state === "active");

  // PANEL FIX 1: Auto-close the AV stream panel when all sessions end.
  // When a remote peer ends the call, the local participant is left on the
  // "No active streams" empty state with showStreamPanel still true, blocking
  // panel switching.  Dispatch av-stream-panel-close so the UI falls through
  // to ChatPanel automatically.
  // IMPORTANT: Only fire when sessions TRANSITION from >0 → 0 (call ended),
  // NOT when the panel is first opened with 0 sessions (which would immediately
  // close the panel the user just opened).
  const prevActiveCountRef = useRef(activeSessions.length);
  useEffect(() => {
    const prevCount = prevActiveCountRef.current;
    prevActiveCountRef.current = activeSessions.length;
    if (activeSessions.length === 0 && prevCount > 0 && session) {
      window.dispatchEvent(new CustomEvent("av-stream-panel-close"));
    }
  }, [activeSessions.length, session]);

  // Group sessions by sessionId
  const sessionGroups = new Map<string, typeof activeSessions>();
  for (const s of activeSessions) {
    const existing = sessionGroups.get(s.sessionId) || [];
    existing.push(s);
    sessionGroups.set(s.sessionId, existing);
  }

  // Use first session group as primary
  const firstGroupEntry = sessionGroups.size > 0 ? Array.from(sessionGroups.entries())[0] : null;
  const primarySessionId = firstGroupEntry?.[0] ?? "";
  const primaryParticipants = firstGroupEntry?.[1] ?? [];
  const primarySession = primaryParticipants[0]?.session ?? null;
  const isGroupCall = primarySession?.isGroupCall ?? false;

  // Build flat list: all participants + self
  const allParticipants: Array<{
    peerAddress: string;
    contactName: string;
    contactInitials: string;
    contactProfile?: RemotePeerProfile;
    isLocal: boolean;
    mediaSession: MediaSession;
  }> = [];

  if (primarySession && session) {
    // Remote participants
    for (const p of primaryParticipants) {
      allParticipants.push({
        peerAddress: p.peerAddress,
        contactName: p.contactName,
        contactInitials: p.contactInitials,
        contactProfile: p.contactProfile,
        isLocal: false,
        mediaSession: p.session,
      });
    }
    // Self
    allParticipants.push({
      peerAddress: session.address,
      contactName: "You",
      contactInitials: session.name.slice(0, 2).toUpperCase(),
      contactProfile: { avatarColor: userPreferences.avatarColor, avatarEmoji: userPreferences.avatarEmoji } as any,
      isLocal: true,
      mediaSession: primarySession,
    });
  }

  // Auto-select first remote participant if none selected
  const effectiveSelected =
    selectedPeerAddress && allParticipants.some((p) => p.peerAddress === selectedPeerAddress)
      ? selectedPeerAddress
      : allParticipants.find((p) => !p.isLocal)?.peerAddress ?? allParticipants[0]?.peerAddress ?? null;

  const selectedParticipant = allParticipants.find((p) => p.peerAddress === effectiveSelected) ?? null;

  // Filmstrip visibility: hide self when muted AND enough participants
  const FILMSTRIP_SCREEN_THRESHOLD = 4;
  const filmstripParticipants = allParticipants.filter((p) => {
    if (!p.isLocal) return true;
    // Show self if unmuted OR too few participants
    if (!isMicMuted) return true;
    return allParticipants.length <= FILMSTRIP_SCREEN_THRESHOLD;
  });

  // Duration timer
  useEffect(() => {
    if (!primarySession) return;
    const interval = setInterval(() => {
      const ms = primarySession.startTime ? Date.now() - primarySession.startTime : 0;
      const min = Math.floor(ms / 60000);
      const sec = Math.floor((ms % 60000) / 1000);
      setCallDuration(`${min}:${sec.toString().padStart(2, "0")}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [primarySession]);

  // Sync video/screen state from session
  useEffect(() => {
    if (primarySession) {
      setIsVideoEnabled(primarySession.videoEnabled ?? false);
      setIsScreenSharing(primarySession.screenShareEnabled ?? false);
    }
  }, [primarySession?.videoEnabled, primarySession?.screenShareEnabled]);

  // Listen for external toggle events
  useEffect(() => {
    const onVideo = (e: CustomEvent) => {
      if (e.detail.sessionId === primarySessionId) setIsVideoEnabled(e.detail.enabled);
    };
    const onScreen = (e: CustomEvent) => {
      if (e.detail.sessionId === primarySessionId) setIsScreenSharing(e.detail.enabled);
    };
    window.addEventListener("av-video-toggled", onVideo as EventListener);
    window.addEventListener("av-screen-share-toggled", onScreen as EventListener);
    return () => {
      window.removeEventListener("av-video-toggled", onVideo as EventListener);
      window.removeEventListener("av-screen-share-toggled", onScreen as EventListener);
    };
  }, [primarySessionId]);

  // ── Control handlers ──

  const toggleMic = useCallback(() => {
    if (!primarySession?.localStream) return;
    const tracks = primarySession.localStream.getAudioTracks();
    tracks.forEach((t) => (t.enabled = !t.enabled));
    setIsMicMuted(!tracks[0]?.enabled);
  }, [primarySession]);

  const toggleVideo = useCallback(async () => {
    if (!primaryParticipants[0] || isTogglingVideo) return;
    setIsTogglingVideo(true);
    try {
      const peer = node.getOrCreatePeer(primaryParticipants[0].peerAddress);
      const newState = !isVideoEnabled;
      const ok = await peer.avStreamManager.toggleVideo(primarySessionId, newState);
      if (ok) setIsVideoEnabled(newState);
    } catch (err) {
      console.error("[AV_PANEL] toggleVideo error", err);
    } finally {
      setIsTogglingVideo(false);
    }
  }, [primaryParticipants, primarySessionId, isVideoEnabled, isTogglingVideo, node]);

  const toggleScreenShare = useCallback(async () => {
    if (!primaryParticipants[0] || isTogglingScreenShare) return;
    setIsTogglingScreenShare(true);
    try {
      const peer = node.getOrCreatePeer(primaryParticipants[0].peerAddress);
      const newState = !isScreenSharing;
      const ok = await peer.avStreamManager.toggleScreenShare(primarySessionId, newState);
      if (ok) {
        setIsScreenSharing(newState);
        if (newState) setIsVideoEnabled(false);
      }
    } catch (err) {
      console.error("[AV_PANEL] toggleScreenShare error", err);
    } finally {
      setIsTogglingScreenShare(false);
    }
  }, [primaryParticipants, primarySessionId, isScreenSharing, isTogglingScreenShare, node]);

  const endCall = useCallback(async () => {
    if (!primaryParticipants[0]) return;
    try {
      const peer = node.getOrCreatePeer(primaryParticipants[0].peerAddress);
      await peer.avStreamManager.endAudioStream(primarySessionId, "hangup");
      window.dispatchEvent(new CustomEvent("av-stream-panel-close"));
    } catch (err) {
      console.error("[AV_PANEL] endCall error", err);
    }
  }, [primaryParticipants, primarySessionId, node]);

  // ── Empty states ──

  if (!session) {
    return (
      <div className="bg-surface rounded-t-large sm:rounded-tr-large relative flex flex-1 flex-col items-center justify-center overflow-x-hidden rounded-tr-none sm:mr-6">
        <div className="text-center space-y-4 max-w-md px-8">
          <div className="bg-outline/12 rounded-full p-6 mx-auto w-24 h-24 flex items-center justify-center">
            <PhoneOff className="w-12 h-12 text-outline" />
          </div>
          <h3 className="text-title-lg text-on-surface">Not connected</h3>
          <p className="text-body-md text-on-surface-variant">Session not available.</p>
        </div>
      </div>
    );
  }

  if (activeSessions.length === 0 || !primarySession) {
    return (
      <div className="bg-surface rounded-t-large sm:rounded-tr-large relative flex flex-1 flex-col items-center justify-center overflow-x-hidden rounded-tr-none sm:mr-6">
        <div className="text-center space-y-4 max-w-md px-8">
          <div className="bg-outline/12 rounded-full p-6 mx-auto w-24 h-24 flex items-center justify-center">
            <PhoneIcon className="w-12 h-12 text-outline" />
          </div>
          <h3 className="text-title-lg text-on-surface">No active streams</h3>
          <p className="text-body-md text-on-surface-variant">
            Start a call from the A/V Stream drawer to see it here.
          </p>
        </div>
      </div>
    );
  }

  // ── Active call panel ──

  return (
    <div className="bg-surface rounded-t-large sm:rounded-tr-large relative flex flex-1 flex-col overflow-hidden rounded-tr-none sm:mr-6">
      {/* ── Header ── */}
      <div className="bg-surface-container-highest rounded-t-large sm:rounded-tr-large flex min-w-0 shrink-0 items-center justify-between gap-4 rounded-tr-none px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <button
            onClick={endCall}
            className="grid size-9 shrink-0 place-items-center rounded-full bg-error text-on-error hover:bg-error/80 transition-colors"
            title="End Call"
          >
            <PhoneOffIcon size={16} />
          </button>
          <div className="min-w-0">
            <h2 className="text-title-lg -mb-1 block truncate">
              {isGroupCall
                ? `Group Call`
                : `${primaryParticipants[0]?.contactName || "Call"}`}
            </h2>
            <div className="flex items-center gap-2">
              <code className="bg-surface-container text-on-surface rounded-extra-small truncate px-1 py-0.5 text-xs">
                {primarySessionId.slice(0, 8)}
              </code>
              {isGroupCall && (
                <span className="text-label-sm text-on-surface-variant">
                  {allParticipants.length} participants
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {isGroupCall && primarySession.initiatorAddress === session.address && (
            <span className="inline-flex items-center gap-1 text-tertiary text-label-sm mr-1">
              <CrownIcon size={14} />
            </span>
          )}
          <span className="text-label-lg text-on-surface tabular-nums font-medium mr-2">{callDuration}</span>

          {/* ── Inline controls ── */}

          {/* Mute / Unmute */}
          <button
            onClick={toggleMic}
            className={`grid size-9 place-items-center rounded-full transition-colors ${
              isMicMuted
                ? "bg-error text-on-error"
                : "bg-surface-container text-on-surface hover:bg-surface-container-high"
            }`}
            title={isMicMuted ? "Unmute" : "Mute"}
          >
            {isMicMuted ? <MicOffIcon size={16} /> : <MicIcon size={16} />}
          </button>

          {/* Video */}
          <button
            onClick={toggleVideo}
            disabled={isTogglingVideo}
            className={`grid size-9 place-items-center rounded-full transition-colors ${
              isVideoEnabled
                ? "bg-primary text-on-primary"
                : "bg-surface-container text-on-surface hover:bg-surface-container-high"
            } ${isTogglingVideo ? "opacity-50 cursor-wait" : ""}`}
            title={isVideoEnabled ? "Turn Off Camera" : "Turn On Camera"}
          >
            {isVideoEnabled ? <VideoIcon size={16} /> : <VideoOffIcon size={16} />}
          </button>

          {/* Screen Share */}
          <button
            onClick={toggleScreenShare}
            disabled={isTogglingScreenShare}
            className={`grid size-9 place-items-center rounded-full transition-colors ${
              isScreenSharing
                ? "bg-tertiary text-on-tertiary"
                : "bg-surface-container text-on-surface hover:bg-surface-container-high"
            } ${isTogglingScreenShare ? "opacity-50 cursor-wait" : ""}`}
            title={isScreenSharing ? "Stop Sharing" : "Share Screen"}
          >
            <MonitorUp size={16} />
          </button>

          {/* Record (placeholder) */}
          <button
            disabled
            className="grid size-9 place-items-center rounded-full bg-surface-container text-on-surface-variant opacity-50 cursor-not-allowed"
            title="Record (coming soon)"
          >
            <CircleDot size={16} />
          </button>

          {/* Settings */}
          <AVSettingsDialog>
            <mui.Dialog.Trigger
              className="grid size-9 place-items-center rounded-full bg-surface-container text-on-surface-variant hover:bg-surface-container-high transition-colors"
              title="A/V Settings"
            >
              <Settings size={16} />
            </mui.Dialog.Trigger>
          </AVSettingsDialog>

        </div>
      </div>

      {/* ── Main stream area ── */}
      <div className="relative flex-1 bg-black">
        {selectedParticipant && (
          <MainStreamView
            session={selectedParticipant.mediaSession}
            peerAddress={selectedParticipant.peerAddress}
            contactName={selectedParticipant.contactName}
            contactInitials={selectedParticipant.contactInitials}
            contactProfile={selectedParticipant.contactProfile}
            isLocal={selectedParticipant.isLocal}
          />
        )}

        {/* ── Filmstrip overlay ── */}
        {showFilmstrip && filmstripParticipants.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 bg-black/30 backdrop-blur-md rounded-xl px-3 py-2">
            {filmstripParticipants.map((p) => (
              <FilmstripTile
                key={p.peerAddress}
                session={p.mediaSession}
                peerAddress={p.peerAddress}
                contactName={p.contactName}
                contactInitials={p.contactInitials}
                contactProfile={p.contactProfile}
                isLocal={p.isLocal}
                isSelected={p.peerAddress === effectiveSelected}
                isMuted={p.isLocal ? isMicMuted : false}
                onClick={() => setSelectedPeerAddress(p.peerAddress)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
